import React, { type ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { EmojiPickerCombo, type EmojiPickerComboProps } from '@pkg/components';
import { Form } from '@pkg/components/Modal/styles';
import { Button } from '@pkg/components/Sections/Profile/Buttons';
import { Sizes } from '@pkg/components/Sections/Profile/Buttons/Button/types';
import { DepInput } from '@pkg/components/deprecated';
import { useMediaQuery } from '@pkg/providers';

import config from '@app/config';

import { PrivacyOptions } from '../PrivacyOptions';

import { ButtonWrapper, InputWrapper } from './styles';

const POD_NAME_LIMIT = config.podNameLimit;

interface Props {
    handleCreate(): void;
    setPopoverFullyOpen(val: boolean): void;
    setWishlistName: React.Dispatch<React.SetStateAction<string>>;
    wishlistName: string;
    setIsPrivate: React.Dispatch<React.SetStateAction<boolean>>;
    isPrivate: boolean;
    isLoading: boolean;
    disabled?: boolean;
    isEdit?: boolean;
}

export const CreateWishlistContent = ({
    handleCreate,
    setPopoverFullyOpen,
    setWishlistName,
    wishlistName,
    disabled = false,
    isEdit = false,
    setIsPrivate,
    isPrivate,
    isLoading,
}: Props) => {
    const { t: translate } = useTranslation(['nft_details']);

    const [emojiBoxRight, setEmojiBoxRight] = useState(0);
    const [emojiBoxTop, setEmojiBoxTop] = useState(0);
    const inputRef = useRef<HTMLDivElement>(null);
    const { desktop } = useMediaQuery();

    const handleEmojiSelect: EmojiPickerComboProps['onSelectEmoji'] = (emoji) => {
        setWishlistName((value) => `${value}${emoji}`);
    };

    const handlePopoverAnimate: NonNullable<EmojiPickerComboProps['onPopoverAnimate']> = (definition) => {
        // TODO: It would be ideal to get an enum from framer-motion
        if (definition === 'show') {
            setPopoverFullyOpen(true);
        } else {
            setPopoverFullyOpen(false);
        }
    };

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.value.length <= POD_NAME_LIMIT) setWishlistName(event.target.value);
    };

    const handleRadioChange = (value: boolean) => {
        setIsPrivate(value);
    };

    const emojiPickerMarkup = desktop ? (
        <EmojiPickerCombo
            closeExclusions={[inputRef.current]}
            onSelectEmoji={handleEmojiSelect}
            onPopoverAnimate={handlePopoverAnimate}
            top={emojiBoxTop}
            right={emojiBoxRight}
        />
    ) : null;

    useEffect(() => {
        if (inputRef && inputRef.current) {
            const el = inputRef.current.getBoundingClientRect();
            // 10 pixels offset to the left
            setEmojiBoxRight(el.right + 10);
            // 100 pixels offset upwards
            setEmojiBoxTop(el.top - 100);
        }
    }, [inputRef]);

    const handleSubmit = useCallback(
        (evt: React.FormEvent) => {
            evt.preventDefault();
            handleCreate();
        },
        [handleCreate],
    );

    return (
        <Form onSubmit={handleSubmit}>
            <InputWrapper ref={inputRef}>
                <DepInput
                    disabled={disabled}
                    label={translate('detail_view_wishlist_label_edit')}
                    value={wishlistName}
                    append={emojiPickerMarkup}
                    isInvalid={false}
                    onChange={handleInputChange}
                />
            </InputWrapper>
            <PrivacyOptions
                heading={translate('detail_view_wishlist_privacy_heading')}
                publicDescription={translate('detail_view_wishlist_privacy_public_desc')}
                privateDescription={translate('detail_view_wishlist_privacy_private_desc')}
                isPrivate={isPrivate}
                handleRadioChange={handleRadioChange}
            />
            <ButtonWrapper>
                <Button disabled={wishlistName.length < 2} size={Sizes.large} loading={isLoading}>
                    {translate(isEdit ? 'detail_view_wishlist_cta' : 'detail_view_wishlist_cta_create')}
                </Button>
            </ButtonWrapper>
        </Form>
    );
};
